/* eslint-disable no-useless-catch */
import get from 'lodash/get';
import moment from 'moment';
import {
  BusinessLevels,
  MISReport,
  MISReportSummaryResponse,
} from '../../common/types';
import { MisSummaryEntity } from '../../common/entity';
import {
  CursorAccess,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  MIS_SUMMARY_INDEX,
  QueryType,
} from '../../db/constants';
import { getCursor } from '../../db/db';
import { appConfig } from '../../config/app-config';
import { callApi } from '../../common/api';
import { API_END_POINTS } from '../../config/api-endpoints';
import { createOrUpdateMisSummary } from '../../db/mis-summary';

export const getMisSummary = async (
  invoiceStartDate: string,
  invoiceEndDate: string,
  orgId: string
): Promise<MisSummaryEntity | undefined> => {
  try {
    const startDate = moment(invoiceStartDate, DATE_TIME_FORMAT);
    const endDate = moment(invoiceEndDate, DATE_TIME_FORMAT);
    let cursor = await getCursor(
      appConfig.IDB.STORE.MIS_SUMMARY,
      MIS_SUMMARY_INDEX.ORG_ID_INDEX.NAME,
      orgId,
      CursorAccess.READ_ONLY,
      QueryType.ONLY
    );

    while (cursor) {
      if (
        moment(cursor.value.invoiceStartDate).format(DATE_FORMAT) ===
          startDate.format(DATE_FORMAT) &&
        moment(cursor.value.invoiceEndDate).format(DATE_FORMAT) ===
          endDate.format(DATE_FORMAT)
      ) {
        return cursor.value;
      }
      // eslint-disable-next-line no-await-in-loop
      cursor = await cursor.continue();
    }
  } catch (e) {
   //  console.log('Failed to get MIS Summary from db', e);
  }
};

const getSummaryFromApi = async (
  invoiceStartDate: string,
  invoiceEndDate: string,
  businessLevel: BusinessLevels,
  businessName: string,
  panNumber: string,
  orgId: string,
  businessId: string,
) => {
  const filter = {
    invoiceStartDate,
    invoiceEndDate,
  };

  const headers = {
    PAN: panNumber,
    'node-id': businessId,
    'node-type': businessLevel,
  };

  const response = await callApi({
    method: 'POST',
    url: API_END_POINTS.EINVOICE.SUMMARY,
    data: { filter },
    headers,
  });

  const summaryData = response.data as MISReportSummaryResponse;

  const businessSummaryReport = summaryData.MisReport.filter(
    (misSummary: MISReport) =>
      misSummary.Level === businessLevel && misSummary.Name === businessName
  )[0];

  const businessSummary = {
    totalPendingIrnGeneration: get(
      businessSummaryReport,
      'ReportsData.NotGenerated.Count',
      0
    ),
    totalNoOfInvoices: get(businessSummaryReport, 'ReportsData.Total.Count', 0),
    totalIrnGenerated: get(
      businessSummaryReport,
      'ReportsData.Generated.Count',
      0
    ),
    totalIrnFailed: get(businessSummaryReport, 'ReportsData.Failed.Count', 0),
    totalIrnCancelled: get(
      businessSummaryReport,
      'ReportsData.Cancelled.Count',
      0
    ),
  };

  createOrUpdateMisSummary(
    invoiceStartDate,
    invoiceEndDate,
    orgId,
    businessSummary
  );

  return businessSummary;
};

export const getSummary = async (
  orgId: string,
  invoiceEndDate: string,
  invoiceStartDate: string,
  businessLevel: BusinessLevels,
  businessName: string,
  panNumber: string,
  businessId: string,
) => {
  try {
    const responseFromDb = await getMisSummary(
      invoiceStartDate,
      invoiceEndDate,
      orgId
    );

    const responseFromApi = getSummaryFromApi(
      invoiceStartDate,
      invoiceEndDate,
      businessLevel,
      businessName,
      panNumber,
      orgId,
      businessId
    );

    if (responseFromDb) {
      // console.log('From db');
      return {
        totalIrnCancelled: responseFromDb.totalIrnCancelled,
        totalIrnFailed: responseFromDb.totalIrnFailed,
        totalIrnGenerated: responseFromDb.totalIrnGenerated,
        totalNoOfInvoices: responseFromDb.totalNoOfInvoices,
        totalPendingIrnGeneration: responseFromDb.totalPendingIrnGeneration,
      };
    }
   //  console.log('From api');
    const response = await responseFromApi;
    return response;
  } catch (error) {
    // console.log(error);
    throw error;
  }
};
